import React, { useEffect, useState } from 'react';
import { Shield, Users, Award, Heart, Clock, CheckCircle } from 'lucide-react';
import { SiteContent } from './admin/types';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const AnimatedCard: React.FC<{ children: React.ReactNode; delay?: number }> = ({ children, delay = 0 }) => {
  const { ref, isVisible } = useScrollAnimation();
  return (
    <div 
      ref={ref} 
      className={`transition-all duration-500 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const About: React.FC = () => {
  const [content, setContent] = useState<SiteContent['about']>({
    title: 'Why Choose Our Medicare Team?',
    subtitle: 'We\'re dedicated to simplifying Medicare for you. Our experienced team provides personalized guidance to help you make informed decisions about your healthcare coverage.',
    trustTitle: 'Trusted by Thousands of Medicare Recipients',
    trustDescription: 'Our clients trust us because we put their needs first. We\'re committed to providing honest, transparent guidance that helps you find the best Medicare coverage for your situation.',
    benefits: {
      professional: { title: 'Professional Guidance', description: 'Licensed Medicare specialists with deep knowledge of all plan types and regulations.' },
      personalized: { title: 'Personalized Service', description: 'One-on-one consultations tailored to your specific healthcare needs and budget.' },
      proven: { title: 'Proven Track Record', description: 'Over 5,000 satisfied clients and 15+ years of combined Medicare experience.' },
      compassionate: { title: 'Compassionate Care', description: 'We understand healthcare decisions are personal and provide supportive guidance.' },
      available: { title: 'Always Available', description: 'Available during enrollment periods and ongoing assistance year-round.' },
      noCost: { title: 'No-Cost Consultation', description: 'All our Medicare consultation services are completely free to you.' }
    },
    credentials: {
      licensed: 'Licensed Agents',
      certified: 'Medicare Certified'
    }
  });

  useEffect(() => {
    const loadContent = async () => {
      try {
        const response = await fetch('/data/content.json');
        if (response.ok) {
          const data = await response.json();
          if (data.about) {
            setContent(data.about);
          }
        }
      } catch (error) {
        console.error('Error loading about content:', error);
      }
    };
    loadContent();
  }, []);
  const benefits = [
    {
      icon: <Shield className="text-bbha-plum-700" size={32} />,
      title: content.benefits?.professional?.title || 'Professional Guidance',
      description: content.benefits?.professional?.description || 'Licensed Medicare specialists with deep knowledge of all plan types and regulations.'
    },
    {
      icon: <Users className="text-bbha-olive-700" size={32} />,
      title: content.benefits?.personalized?.title || 'Personal Guidance',
      description: content.benefits?.personalized?.description || 'One-on-one consultations tailored to your specific healthcare needs and budget.'
    },
    {
      icon: <Award className="text-bbha-olive-600" size={32} />,
      title: content.benefits?.proven?.title || 'Proven Track Record',
      description: content.benefits?.proven?.description || 'Over 5,000 satisfied clients and 15+ years of combined Medicare experience.'
    },
    {
      icon: <Heart className="text-bbha-olive-600" size={32} />,
      title: content.benefits?.compassionate?.title || 'Compassionate Care',
      description: content.benefits?.compassionate?.description || 'We understand healthcare decisions are personal and provide supportive guidance.'
    },
    {
      icon: <Clock className="text-bbha-olive-600" size={32} />,
      title: content.benefits?.available?.title || 'Always Available',
      description: content.benefits?.available?.description || 'Available during enrollment periods and ongoing assistance year-round.'
    },
    {
      icon: <CheckCircle className="text-bbha-olive-600" size={32} />,
      title: content.benefits?.noCost?.title || 'No-Cost Consultation',
      description: content.benefits?.noCost?.description || 'All our Medicare consultation services are completely free to you.'
    }
  ];

  return (
    <section id="about" className="py-16 lg:py-24 bg-gradient-to-br from-bbha-cream-50 to-bbha-olive-50 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-gradient-to-r from-bbha-plum-50/30 to-bbha-olive-50/30"></div>
      
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Header - Enhanced typography */}
        <div className="text-center mb-16 lg:mb-20">
          <h2 id="about-heading" className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-gray-900 mb-6 lg:mb-8 leading-tight">
            {content.title}
          </h2>
          <p className="text-xl sm:text-2xl lg:text-3xl text-gray-600 max-w-4xl mx-auto leading-relaxed font-light">
            {content.subtitle}
          </p>
        </div>

        {/* Statistics Section - Enhanced with animations */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 lg:gap-12 mb-16 lg:mb-20 max-w-4xl mx-auto" role="region" aria-labelledby="stats-heading">
          <h3 id="stats-heading" className="sr-only">Company Statistics</h3>
          <AnimatedCard delay={0}>
            <div className="text-center group">
              <div className="bg-bbha-cream-100 border border-bbha-plum-200 rounded-2xl p-6 lg:p-8 shadow-lg hover:shadow-xl transition-all duration-500 transform hover:-translate-y-2">
                <div className="text-4xl sm:text-5xl lg:text-6xl font-bold text-bbha-plum-700 mb-3 group-hover:scale-110 transition-transform duration-300">
                  {content.stats?.clients || '500+'}
                </div>
                <div className="text-lg lg:text-xl text-bbha-plum-600 font-medium">
                  {content.stats?.clientsLabel || 'Happy Clients'}
                </div>
              </div>
            </div>
          </AnimatedCard>
          <AnimatedCard delay={100}>
            <div className="text-center group">
              <div className="bg-bbha-cream-100 border border-bbha-olive-200 rounded-2xl p-6 lg:p-8 shadow-lg hover:shadow-xl transition-all duration-500 transform hover:-translate-y-2">
                <div className="text-4xl sm:text-5xl lg:text-6xl font-bold text-bbha-olive-700 mb-3 group-hover:scale-110 transition-transform duration-300">
                  {content.stats?.experience || '36+'}
                </div>
                <div className="text-lg lg:text-xl text-bbha-olive-600 font-medium">
                  {content.stats?.experienceLabel || 'Years Experience'}
                </div>
              </div>
            </div>
          </AnimatedCard>
          <AnimatedCard delay={200}>
            <div className="text-center group">
              <div className="bg-bbha-cream-100 border border-bbha-plum-200 rounded-2xl p-6 lg:p-8 shadow-lg hover:shadow-xl transition-all duration-500 transform hover:-translate-y-2">
                <div className="text-4xl sm:text-5xl lg:text-6xl font-bold text-bbha-plum-700 mb-3 group-hover:scale-110 transition-transform duration-300">
                  {content.stats?.satisfaction || '100%'}
                </div>
                <div className="text-lg lg:text-xl text-bbha-plum-600 font-medium">
                  {content.stats?.satisfactionLabel || 'Satisfaction Rate'}
                </div>
              </div>
            </div>
          </AnimatedCard>
        </div>

        {/* Benefits Grid - Enhanced with stagger animations */}
        <div className="mb-16 lg:mb-20">
          <h3 id="benefits-heading" className="sr-only">Our Benefits and Services</h3>
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10 max-w-6xl mx-auto list-none" role="list" aria-labelledby="benefits-heading">
            {benefits.map((benefit, index) => (
              <li key={index}>
                <AnimatedCard delay={index * 100}>
                  <div className="group bg-bbha-cream-100 rounded-2xl p-8 lg:p-10 shadow-lg hover:shadow-2xl transition-all duration-500 border border-bbha-plum-200 hover:border-bbha-plum-400 transform hover:-translate-y-3">
                  <div className="mb-6 flex justify-center group-hover:scale-110 transition-transform duration-300" aria-hidden="true">
                    <div className={`p-4 rounded-2xl ${
                      index % 2 === 0 
                        ? 'bg-gradient-to-br from-bbha-plum-600 to-bbha-plum-700' 
                        : 'bg-gradient-to-br from-bbha-olive-600 to-bbha-olive-700'
                    }`}>
                      {React.cloneElement(benefit.icon as React.ReactElement, { size: 40, className: 'text-white' })}
                    </div>
                  </div>
                  <h4 className="text-xl lg:text-2xl xl:text-3xl font-bold text-gray-900 mb-4 lg:mb-6 text-center group-hover:text-bbha-plum-700 transition-colors duration-300">
                    {benefit.title}
                  </h4>
                  <p className="text-lg lg:text-xl text-gray-600 leading-relaxed text-center font-light">
                    {benefit.description}
                  </p>
                  </div>
                </AnimatedCard>
              </li>
            ))}
          </ul>
        </div>

        {/* Trust Section - Enhanced with better mobile design */}
        <AnimatedCard>
          <div className="bg-gradient-to-r from-bbha-cream-100 to-bbha-olive-100 rounded-3xl p-8 lg:p-16 shadow-xl">
            <div className="text-center">
              <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 lg:mb-8 leading-tight">
                {content.trustTitle}
              </h3>
              <p className="text-lg sm:text-xl lg:text-2xl text-gray-600 mb-8 lg:mb-12 max-w-4xl mx-auto leading-relaxed font-light">
                {content.trustDescription}
              </p>
              
              {/* Credentials Row - Enhanced with better spacing */}
              <div className="flex flex-col sm:flex-row justify-center items-center gap-6 lg:gap-12">
                <div className="flex items-center gap-4 bg-bbha-cream-100 border border-bbha-olive-300 rounded-full px-6 py-4 lg:px-8 lg:py-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                  <div className="p-2 bg-bbha-olive-600 rounded-full">
                    <Shield className="text-white" size={24} />
                  </div>
                  <span className="text-lg lg:text-xl font-bold text-bbha-olive-800">{content.credentials?.licensed || 'Licensed Agents'}</span>
                </div>
                <div className="flex items-center gap-4 bg-bbha-cream-100 border border-bbha-plum-300 rounded-full px-6 py-4 lg:px-8 lg:py-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                  <div className="p-2 bg-bbha-plum-600 rounded-full">
                    <Award className="text-white" size={24} />
                  </div>
                  <span className="text-lg lg:text-xl font-bold text-bbha-plum-800">{content.credentials?.certified || 'Medicare Certified'}</span>
                </div>
              </div>
            </div>
          </div>
        </AnimatedCard>
      </div>
    </section>
  );
};

export default About;